import dayjs from 'dayjs';
import { useState } from 'react';

import { CREATE_ROUTE } from 'services/constants';
import { useGet, useGetWithRefetch } from 'services/Http';
import {
    parentNamesInEffect,
    therapyGroupLinksUrl,
    therapyGroupUrl,
} from 'services/TherapyGroupHelpers';
import {
    preferencesFromWindows,
    preferenceWindowsFromResponse,
    therapyGroupPreferenceWindowsUrl,
    windowsInEffect,
    windowStartFromDay,
} from 'services/TherapyGroupPreferenceHelpers';

// The as-of date is a preview. Windows and links load once; the viewed day only filters them.
export const useTherapyGroupAsOf = (therapyGroupName) => {
    const isCreate = therapyGroupName === CREATE_ROUTE;
    const [day, setDay] = useState(windowStartFromDay(dayjs()));
    const groupUrl = (urlFor) => (isCreate ? null : urlFor(therapyGroupName));
    const { result: windowResponse, refetch: refetchWindows } = useGetWithRefetch(
        groupUrl(therapyGroupPreferenceWindowsUrl),
    );
    const { result: linkResponse, refetch: refetchLinks } = useGetWithRefetch(
        groupUrl(therapyGroupLinksUrl),
    );
    // Today means now, not midnight: a change saved today starts at the moment it was saved,
    // so midnight would hide it until tomorrow. The moment is taken again on every render,
    // so a save shows as soon as the refetch lands. Other days are viewed from their start.
    const now = dayjs().toISOString();
    const isViewingToday = dayjs(day).isSame(now, 'day');
    const viewedAt = isViewingToday ? now : day;
    const windows = preferenceWindowsFromResponse(windowResponse);
    const todayWindows = windowsInEffect(windows, now);
    const viewedWindows = windowsInEffect(windows, viewedAt);
    const todayLinkNames = parentNamesInEffect(linkResponse, now);
    const viewedLinkNames = parentNamesInEffect(linkResponse, viewedAt);
    const todayPreferences = preferencesFromWindows(todayWindows);
    const viewedPreferences = preferencesFromWindows(viewedWindows);
    const therapyGroup = useGet(groupUrl(therapyGroupUrl));
    const refetch = () => {
        refetchWindows();
        refetchLinks();
    };
    return {
        asOf: { day, setDay, isViewingToday },
        therapyGroup,
        todayLinkNames,
        viewedLinkNames,
        todayPreferences,
        viewedPreferences,
        windows,
        viewedWindows,
        windowsLoaded: isCreate || windowResponse !== undefined,
        linksLoaded: isCreate || linkResponse !== undefined,
        refetch,
    };
};
